import { Component, Vue } from 'vue-property-decorator';

import { cloneDeep } from 'lodash';
import Event from '@vertical-plus/vue-js-micro-service-components/lib/model/event/event';
import EventBus from '@vertical-plus/vue-js-core/dist/util/event/event-bus';
import FooterComponent from '@/components/footer/index.vue';
import HeaderComponent from '@/components/header/index.vue';
import JobPreview from '@/components/job-preview/index.vue';
import JobService from '@/model/service/job/job-service';
import MeetingPopupForm from '@/components/meeting-popup-form/index.vue';
import NavbarComponent from '@/components/navbar/index.vue';
import Techspec from '@/model/entity/job/techspec';
import TechspecDto from '@/model/entity/job/techspec-dto';

@Component({
    components:
    {
        'app-footer': FooterComponent,
        'app-header': HeaderComponent,
        'app-job-preview': JobPreview,
        'app-meeting-popup-form': MeetingPopupForm,
        'app-navbar': NavbarComponent,
    },
})
export default class App extends Vue
{
    private jobPreviewOpen = false;
    private previewJobId: number|null = null;

    private meetingPopupOpen = false;
    private popupMeeting: Techspec|null = null;

    $refs!:
    {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        confirmDialog: any
    };

    private created()
    {
        this.unregisterServiceWorkers();

        this.initEvents();
    }

    /**
     * Unregisters any existing service workers
     */

    private async unregisterServiceWorkers()
    {
        if ('serviceWorker' in navigator)
        {
            const registrations = await navigator.serviceWorker.getRegistrations();

            for (const registration of registrations)
            {
                registration.unregister();
            }
        }
    }

    /**
     * Initialises events
     */

    private initEvents()
    {
        EventBus.instance.$on(Event.SessionExpired, () => this.onSessionExpired());

        EventBus.instance.$on('confirm-dialog', (
            title: string, message: string, onConfirm: () => void, warning?: boolean,
        ) => this.handleConfirmDialogEvent(
            title, message, onConfirm, warning,
        ));

        EventBus.instance.$on('preview-job', (jobId: number) => this.previewJob(jobId));
        EventBus.instance.$on('open-job', (jobId: number, newTab: boolean) => this.openJob(jobId, newTab));

        EventBus.instance.$on('create-meeting', () => this.createMeeting());
        EventBus.instance.$on('edit-meeting', (meeting: Techspec) => this.editMeeting(meeting));
    }

    /**
     * Handles session expiry
     */

    private onSessionExpired()
    {
        // Store the current route so we get redirected back to it
        this.$store.commit('authentication/loginRedirectUrl', this.$route.fullPath);

        // Redirect to the home page
        this.$router.replace({
            name: 'home',
        });
    }

    /**
     * Handles Confirm events
     */

    private async handleConfirmDialogEvent(
        title: string, message: string, onConfirm: () => void, warning = false,
    )
    {
        const isConfirmed = await this.$refs.confirmDialog.open(
            title, message, warning,
        );
        if (isConfirmed)
        {
            onConfirm();
        }
    }

    /**
     * Handles job preview requests
     *
     * @param jobId
     */

    private previewJob(jobId: number)
    {
        this.previewJobId = jobId;
        this.jobPreviewOpen = true;
    }

    /**
     * Handles job open requests
     *
     * @param jobId
     * @param newTab
     */

    private openJob(jobId: number, newTab: boolean)
    {
        const location =
        {
            name: 'job-view',
            params: { id: jobId.toString() },
        };

        if (newTab)
        {
            const route = this.$router.resolve(location);
            window.open(route.href, '_blank');
        }
        else
        {
            this.$router.push(location).catch();
        }
    }

    /**
     * Handles requests to create a meeting
     */

    private createMeeting()
    {
        this.popupMeeting = new Techspec(new TechspecDto());
        this.popupMeeting.typeId = JobService.TYPE_MEETING;

        this.meetingPopupOpen = true;
    }

    /**
     * Handles requests to edit a meeting
     *
     * @param meeting
     */

    private editMeeting(meeting: Techspec)
    {
        this.popupMeeting = new Techspec(cloneDeep(meeting.dto));

        this.meetingPopupOpen = true;
    }
}